import type { Membership } from "../../lib/chosen-workspace.js";
import { initialsOf } from "../../lib/initials.js";
import { ChevronsUpDown } from "../icons.js";
import { RailWorkspace } from "../rail-workspace.js";
import { useMenu } from "../use-menu.js";
import { useNewWorkspace, WorkspaceChoices } from "../workspace-choices.js";

/**
 * The workspace, under the wordmark, and the way to every other one
 * (DESIGN.md §5, "The workspace" and "The workspace menu").
 *
 * Its own button, apart from the brand's. The two were one card until
 * 2026-10-13, and a control at the head of the rail that changes everything
 * under it reads as the workspace — so choosing a brand looked as if it moved
 * the research tools and People along with it.
 */
export function WorkspaceSwitch({
  workspaceId,
  workspaceName,
  workspaces,
  reportCount,
  onChooseWorkspace,
}: {
  readonly workspaceId: string;
  readonly workspaceName: string;
  readonly workspaces: readonly Membership[];
  readonly reportCount: number;
  readonly onChooseWorkspace: (workspaceId: string) => void;
}) {
  const menu = useMenu();
  const newWorkspace = useNewWorkspace(onChooseWorkspace);
  // With one workspace there is nothing to list, only a new one to make.
  const others = workspaces.length > 1 ? workspaces : [];

  return (
    <div className="side-workspace-wrap" ref={menu.root} onKeyDown={menu.onKeyDown}>
      <button
        type="button"
        className="side-workspace"
        aria-expanded={menu.open}
        aria-controls="workspace-menu"
        onClick={menu.toggle}
      >
        <span className="side-workspace-tile" aria-hidden="true">
          {initialsOf(workspaceName)}
        </span>
        <span className="side-workspace-name">{workspaceName}</span>
        <ChevronsUpDown className="side-switch-icon" aria-hidden="true" />
        <span className="visually-hidden">, switch workspace</span>
      </button>

      {menu.open ? (
        <div className="account-menu side-menu" id="workspace-menu">
          <RailWorkspace
            workspaceId={workspaceId}
            workspaceName={workspaceName}
            reportCount={reportCount}
          />
          <hr className="account-menu-rule" />
          {others.length > 0 ? <p className="side-menu-label">Workspaces</p> : null}
          <WorkspaceChoices
            current={workspaceId}
            workspaces={others}
            onChoose={(chosen) => {
              menu.close();
              if (chosen !== workspaceId) onChooseWorkspace(chosen);
            }}
            onNew={() => {
              menu.close();
              newWorkspace.start();
            }}
          />
        </div>
      ) : null}
      {newWorkspace.dialog}
    </div>
  );
}
